import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Sparkles, RotateCcw, Heart, PartyPopper, Building2, Tent, HelpCircle, Target, ClipboardList, Mic, Palette, Lightbulb, Star, Music, Users, Drama } from 'lucide-react';
import { services } from '@/data/services';
import { motion, AnimatePresence } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';

interface Question {
  id: string;
  question: string;
  options: { label: string; value: string; tags: string[]; icon: LucideIcon }[];
}

const questions: Question[] = [
  {
    id: 'type',
    question: 'Quel type d\'événement préparez-vous ?',
    options: [
      { label: 'Mariage', value: 'mariage', icon: Heart, tags: ['wedding-planner-designer', 'officiant-ceremonies-laiques', 'musique-ambiance', 'a-la-location', 'lieux-services-associes'] },
      { label: 'Anniversaire / Fête privée', value: 'fete', icon: PartyPopper, tags: ['evenements-publics-prives', 'booking-artistes-spectacles', 'musique-ambiance', 'animations-enfants-famille', 'animations-speciales-experiences', 'a-la-location'] },
      { label: 'Séminaire / Événement pro', value: 'pro', icon: Building2, tags: ['evenements-publics-prives', 'booking-artistes-spectacles', 'musique-ambiance', 'animations-speciales-experiences', 'a-la-location', 'lieux-services-associes'] },
      { label: 'Festival / Événement public', value: 'public', icon: Tent, tags: ['evenements-publics-prives', 'booking-artistes-spectacles', 'musique-ambiance', 'animations-speciales-experiences', 'a-la-location'] },
      { label: 'Je ne sais pas encore', value: 'autre', icon: HelpCircle, tags: services.map(s => s.slug) },
    ],
  },
  {
    id: 'besoin',
    question: 'De quoi avez-vous le plus besoin ?',
    options: [
      { label: 'Organisation complète de A à Z', value: 'orga', icon: Target, tags: ['evenements-publics-prives', 'wedding-planner-designer', 'lieux-services-associes'] },
      { label: 'Coordination le jour J uniquement', value: 'coord', icon: ClipboardList, tags: ['evenements-publics-prives', 'wedding-planner-designer'] },
      { label: 'Un artiste ou un spectacle', value: 'artiste', icon: Mic, tags: ['booking-artistes-spectacles', 'musique-ambiance'] },
      { label: 'Des animations et expériences', value: 'animations', icon: Palette, tags: ['animations-enfants-famille', 'animations-speciales-experiences', 'a-la-location'] },
      { label: 'Des conseils pour m\'organiser seul(e)', value: 'conseil', icon: Lightbulb, tags: ['evenements-publics-prives', 'wedding-planner-designer'] },
    ],
  },
  {
    id: 'ambiance',
    question: 'Quelle ambiance recherchez-vous ?',
    options: [
      { label: 'Élégante & raffinée', value: 'elegante', icon: Star, tags: ['wedding-planner-designer', 'officiant-ceremonies-laiques', 'musique-ambiance', 'booking-artistes-spectacles', 'lieux-services-associes'] },
      { label: 'Festive & dynamique', value: 'festive', icon: Music, tags: ['musique-ambiance', 'booking-artistes-spectacles', 'animations-speciales-experiences', 'a-la-location'] },
      { label: 'Familiale & conviviale', value: 'familiale', icon: Users, tags: ['animations-enfants-famille', 'animations-speciales-experiences', 'a-la-location'] },
      { label: 'Spectaculaire & immersive', value: 'spectaculaire', icon: Drama, tags: ['booking-artistes-spectacles', 'animations-speciales-experiences', 'musique-ambiance'] },
    ],
  },
];

const ServiceFinder = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selectedTags, setSelectedTags] = useState<string[][]>([]);
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (questionId: string, value: string, tags: string[]) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    const newTags = [...selectedTags];
    newTags[step] = tags;
    setSelectedTags(newTags);

    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      setTimeout(() => setShowResults(true), 300);
    }
  };

  const getRecommendedServices = () => {
    const scoreMap: Record<string, number> = {};
    selectedTags.forEach((tags) => {
      tags.forEach((slug) => {
        scoreMap[slug] = (scoreMap[slug] || 0) + 1;
      });
    });
    return services
      .map((s) => ({ ...s, score: scoreMap[s.slug] || 0 }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score);
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setSelectedTags([]);
    setShowResults(false);
  };

  const currentQuestion = questions[step];
  const recommended = showResults ? getRecommendedServices() : [];
  const topServices = recommended.slice(0, 4);
  const otherServices = recommended.slice(4);

  return (
    <section className="px-6 lg:px-8 py-8 md:py-10 bg-background">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-xs font-heading font-semibold mb-4">
            <Sparkles size={14} /> Trouvez votre prestation idéale
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">
            Quel service est fait pour vous ?
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
            Répondez à 3 questions rapides et découvrez les prestations les plus adaptées à votre projet.
          </p>
        </div>

        {!showResults ? (
          <div className="bg-card rounded-2xl border border-border p-6 md:p-10">
            {/* Progress */}
            <div className="flex items-center gap-3 mb-8">
              <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${(step / questions.length) * 100}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground font-heading shrink-0">{step + 1}/{questions.length}</span>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground mb-6">
                  {currentQuestion.question}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentQuestion.options.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(currentQuestion.id, opt.value, opt.tags)}
                      className={`text-left p-4 rounded-xl border transition-all hover:border-primary/50 hover:bg-primary/5 flex items-center gap-3 ${
                        answers[currentQuestion.id] === opt.value
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-background'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <opt.icon size={16} className="text-primary" strokeWidth={1.5} />
                      </div>
                      <span className="text-sm font-heading font-medium text-foreground">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors font-heading"
              >
                <ArrowLeft size={12} /> Question précédente
              </button>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="bg-card rounded-2xl border border-border p-6 md:p-10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-base md:text-lg font-bold text-foreground flex items-center gap-2">
                  <Target size={18} className="text-primary" /> Nos recommandations pour vous
                </h3>
                <button
                  onClick={reset}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors font-heading"
                >
                  <RotateCcw size={12} /> Recommencer
                </button>
              </div>

              {topServices.length > 0 && (
                <>
                  <p className="text-xs font-heading font-semibold uppercase tracking-wider text-primary mb-4">Les plus adaptés à votre projet</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {topServices.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        onClick={() => window.scrollTo(0, 0)}
                        className="group p-5 rounded-xl border-2 border-primary/20 bg-primary/5 hover:border-primary/40 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                            <s.icon size={18} className="text-primary" strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-heading font-bold text-sm text-foreground">{s.title}</h4>
                            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{s.shortDesc}</p>
                            <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary group-hover:gap-2 transition-all">
                              Découvrir <ArrowRight size={12} />
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {otherServices.length > 0 && (
                <>
                  <p className="text-xs font-heading font-semibold uppercase tracking-wider text-muted-foreground mb-3">Également pertinents</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {otherServices.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        onClick={() => window.scrollTo(0, 0)}
                        className="group flex items-center gap-3 p-3 rounded-lg border border-border bg-background hover:border-primary/30 transition-all"
                      >
                        <s.icon size={16} className="text-primary shrink-0" strokeWidth={1.5} />
                        <span className="text-xs font-heading font-medium text-foreground group-hover:text-primary transition-colors truncate">{s.title}</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}

              <div className="mt-8 pt-6 border-t border-border text-center">
                <p className="text-sm text-muted-foreground mb-4">Besoin d'un conseil personnalisé ?</p>
                <Link
                  to="/contact"
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
                >
                  Nous contacter <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ServiceFinder;
